import { api } from "./client";
import type { AssessmentKind, AssignmentScale, CriterionLevel, FeedbackOutcome, RubricCriterion } from "./assignments";
import type { QuizQuestionAward } from "./quizzes";

/**
 * Same-attempt reasoned regrade (S6, #1293). The server derives every term from
 * the attempt: the window frozen at publication, the first release instant and
 * the deadline. A learner reads only their own request and a released decision,
 * and none of a decision's reasons or results while a quiz replacement holds the
 * attempt's result (`held`); the request and its deadline stand meanwhile.
 */
export type RegradeState = "open" | "requested" | "closed" | "not_offered" | "not_graded_work" | "not_released";
export interface RegradeJudgement {
  id: string; event_no: number; outcome: FeedbackOutcome; body: string | null; score: number | null;
  quiz_awards?: Record<string, QuizQuestionAward> | null; visible_at: string | null; created_at: string;
  criterion_scores?: Record<string, number> | null;
  criterion_levels?: Record<string, CriterionLevel> | null;
  selected_scale_row_id?: string | null;
}
export interface RegradeDecision {
  id: string; revision: number; outcome: "unchanged" | "changed"; reason: string; question_reasons: Record<string, string>;
  previous_feedback_id: string; amended_feedback_id: string | null; decided_by: string; decided_at: string;
  released_by: string | null; released_at: string | null; released_feedback_id: string | null;
  /** The saved linked judgement, so a release review shows what was decided rather than today's mark (C2). */
  amended?: RegradeJudgement | null;
}
export interface RegradeQuestion { id: string; position: number; prompt: string; points: number }
export interface RegradeView {
  submission_id: string; course_id: string; assignment_id: string; attempt_number: number | null;
  /** The learner's result is held by an open quiz replacement: no disputed result and no outcome are read (C1). Always false for the teaching team. */
  held: boolean;
  work_title: string; course_code: string; course_title: string; timezone: string; state: RegradeState;
  points: number | null; questions: RegradeQuestion[];
  window_days: number | null; window_source: { kind: "course" | "work"; revision?: number } | null;
  first_released_at: string | null; deadline_at: string | null;
  disputed: RegradeJudgement | null;
  request: { id: string; reason: string; created_at: string; status: "pending" | "decided" } | null;
  outcome: { outcome: "unchanged" | "changed"; reason: string; question_reasons: Record<string, string>; released_at: string; previous: RegradeJudgement; result: RegradeJudgement | null } | null;
  /** The current teaching team's decision work; never present for the learner. */
  teaching?: {
    standing: RegradeJudgement | null; decisions: RegradeDecision[]; changed_questions: string[]; can_decide: boolean;
    /** The current primary may record this attempt's regraded mark, held, while the request has no released decision (C2). */
    can_amend: boolean;
    /** Frozen terms of this attempt, independent of later published corrections. */
    attempt_terms?: { version_id: string; assessment_kind: AssessmentKind; points: number | null; rubric: RubricCriterion[] | null; scale: AssignmentScale | null };
  };
}
export interface WorkRegrades { assignment_id: string; can_decide: boolean; requests: (RegradeView & { learner_name: string | null })[] }
/** `expected_feedback_id` is the attempt's mark on record as the primary read it; a later mark refuses the save (C3). */
export interface DecideRegrade { expected_revision: number; expected_feedback_id: string; outcome: "unchanged" | "changed"; reason: string; question_reasons: Record<string, string> }

const keyed = (key: string) => ({ headers: { "Idempotency-Key": key } });
const id = encodeURIComponent;
export const regradesApi = {
  get: (submission: string) => api.get<RegradeView>(`/submissions/${id(submission)}/regrade`, { cache: "no-store" }),
  mine: () => api.get<{ attempts: RegradeView[] }>("/me/regrades", { cache: "no-store" }),
  forWork: (assignment: string) => api.get<WorkRegrades>(`/assignments/${id(assignment)}/regrades`, { cache: "no-store" }),
  ask: (submission: string, body: { reason: string }, key: string) => api.post<{ state: "confirmed"; request_id: string }>(`/submissions/${id(submission)}/regrade-request`, body, keyed(key)),
  decide: (request: string, body: DecideRegrade, key: string) => api.post<{ state: "confirmed"; decision_id: string; revision: number }>(`/regrade-requests/${id(request)}/decisions`, body, keyed(key)),
  release: (request: string, body: { decision_id: string }, key: string) => api.post<{ state: "confirmed"; outcome: string }>(`/regrade-requests/${id(request)}/release`, body, keyed(key)),
};

export const regradeKeys = {
  attempt: (submission: string) => ["regrade", submission] as const,
  mine: ["regrades", "mine"] as const,
  /** Under Review's own read, so a mark recorded there refreshes the requests too. */
  work: (assignment: string) => ["assignment-review", assignment, "regrades"] as const,
};
